import { useEffect, useRef, useState } from 'react'
import { createJSONStorage } from 'zustand/middleware'
// Direct-module import, not the package barrel — see index.ts's re-export comment for why
// (this package's own standalone tsc build has no Vite alias to bail out '@admin/api',
// which @passport/store's own barrel pulls in via userStore.ts/roleStore.ts).
import { useAuthStore } from '../../../../passport/packages/@passport/store/src/authStore'
import { HttpClient, UserClient, AuthClient } from '@passport/api-client'
import { FAKE_TOKEN_PREFIX } from '@passport/fake-auth'
import { SSO_HANDOFF_ATTEMPT_STORAGE_KEY } from '../components/LoginRedirectPage'

export interface UseSsoHandoffSessionOptions {
  /** This app's own API base URL — passed straight through to the `HttpClient` used to
   *  redeem a handoff code and fetch the user's profile. */
  apiBaseUrl: string
  /**
   * Some apps have API clients that predate the SSO migration and still read a plain
   * `localStorage` key directly instead of `@passport/store`'s Zustand-managed session
   * (e.g. flow-studio's NodeTemplateApiClient / flow-studio-api's baseApiClient reading
   * `'authToken'`). Pass that key here to keep it mirrored in sync with whatever token
   * this hook ends up using — omit entirely for an app with no such legacy clients.
   */
  mirrorLegacyAuthTokenKey?: string
  /**
   * Where the redeemed session lives. `'local'` (default, = behaviour before S8) keeps @passport/store's
   * normal `localStorage` persistence. `'memory'` swaps that store's storage for a per-page in-memory one
   * and wipes any persisted copy, so on a customer custom-domain origin the session (tokens included) never
   * reaches `localStorage` (R-006); a reload then simply asks for a new handoff. `'memory'` also disables
   * `mirrorLegacyAuthTokenKey`.
   */
  persistence?: 'local' | 'memory'
}

/** Minimal in-memory `Storage` shape for zustand's `createJSONStorage`. */
function createInMemoryStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  const values = new Map<string, string>()
  return {
    getItem: (key) => values.get(key) ?? null,
    setItem: (key, value) => {
      values.set(key, value)
    },
    removeItem: (key) => {
      values.delete(key)
    },
  }
}

/** Switches @passport/store's persist layer to memory and removes any copy already in `localStorage`. */
function applyMemoryOnlyPersistence(): void {
  const memoryStorage = createInMemoryStorage()
  // The store's own persist key (no import of @passport/constants: this package's standalone build does not depend on it).
  const storageKey = useAuthStore.persist.getOptions().name
  useAuthStore.persist.setOptions({ storage: createJSONStorage(() => memoryStorage) })
  try {
    if (storageKey) localStorage.removeItem(storageKey)
  } catch {
    // storage unavailable (private mode / blocked): nothing persisted, nothing to wipe
  }
}

export interface UseSsoHandoffSessionResult {
  /** True until the initial session check (existing-session verify, or handoff-code
   *  redemption) has finished — gate first paint on this the same way `isCheckingSession`
   *  was used before this hook existed, to avoid a flash of the login redirect while a
   *  perfectly good persisted session is still being confirmed. */
  isCheckingSession: boolean
}

/**
 * Cross-app SSO session reconciliation — the exchange-code handoff redemption, existing-
 * session verification, and profile-fetch-to-populate-`user` logic every migrated app
 * needs on mount, extracted from what were three near-identical hand-rolled copies
 * (flow-studio, saas-tenant-administration, form-studio's own App.tsx — see git history).
 * Pairs with `<LoginRedirectPage handoffMode="code">` / `<AuthHeaderControls
 * handoffMode="code">` on the same app: this hook is the receiving side of that same
 * `?code=` contract (see `ReturnUrlRedirectHandler.tsx` in the login app for the sending
 * side, and `AuthClient.issueHandoffCode`/`redeemHandoffCode`'s doc comments for the
 * protocol itself).
 *
 * On mount, exactly once (guarded against React.StrictMode's dev-mode double-invoke same
 * as the three original copies were):
 * 1. Reads and immediately strips `?code=` from the address bar — it should never linger
 *    in the URL/history longer than this one moment, redeemed or not.
 * 2. If a `FAKE_TOKEN_PREFIX`-prefixed token is already persisted (see `@passport/fake-
 *    auth`), skips straight to done — it can never pass real backend verification and
 *    trying to out-guess a real backend's response to a token it never issued is pointless.
 * 3. Verifies whatever session is already persisted, if any. A persisted-but-now-invalid
 *    session with NO handoff code present is a stale session left over from an earlier
 *    visit — this clears it via `logout()` so route guards gated on `user` correctly fall
 *    back to a login redirect instead of rendering with a dead token (silent 401s on every
 *    real request while the UI looks logged in). This specific behavior was present in
 *    flow-studio's own copy but NOT in saas-tenant-administration's/form-studio's original
 *    copies — both gain the fix by switching to this hook.
 * 4. Otherwise, redeems the handoff code (if present) for a real token pair, verifies it,
 *    and fetches the profile once to populate `user` — a redeemed/verified token never
 *    went through this app's own login()/setUser(), so without this step the route guard
 *    would bounce straight back to the login redirect despite a genuinely successful
 *    cross-app login.
 */
export function useSsoHandoffSession({
  apiBaseUrl,
  mirrorLegacyAuthTokenKey: requestedMirrorKey,
  persistence = 'local',
}: UseSsoHandoffSessionOptions): UseSsoHandoffSessionResult {
  const memoryOnly = persistence === 'memory'
  // No legacy localStorage mirror on memory-only (custom-host) sessions.
  const mirrorLegacyAuthTokenKey = memoryOnly ? undefined : requestedMirrorKey
  const { token, setToken, setRefreshToken, setExpiresAt, setUser, verifyToken, logout } = useAuthStore()
  const [isCheckingSession, setIsCheckingSession] = useState(true)
  const hasRunSessionCheck = useRef(false)

  useEffect(() => {
    if (hasRunSessionCheck.current) {
      return
    }
    hasRunSessionCheck.current = true

    if (memoryOnly) {
      applyMemoryOnlyPersistence()
    }

    const params = new URLSearchParams(window.location.search)
    const handoffCode = params.get('code')

    if (handoffCode) {
      params.delete('code')
      const rest = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''))
    }

    if (mirrorLegacyAuthTokenKey && token) {
      localStorage.setItem(mirrorLegacyAuthTokenKey, token)
    }

    if (token?.startsWith(FAKE_TOKEN_PREFIX)) {
      setIsCheckingSession(false)
      return
    }

    async function reconcileSession() {
      let usingExistingSession = false
      if (token) {
        try {
          usingExistingSession = await verifyToken()
        } catch {
          usingExistingSession = false
        }
        if (!usingExistingSession && !handoffCode) {
          await logout()
        }
      }

      if (usingExistingSession) {
        sessionStorage.removeItem(SSO_HANDOFF_ATTEMPT_STORAGE_KEY)
        return
      }

      if (!handoffCode) {
        return
      }

      const httpClient = new HttpClient(apiBaseUrl)
      const authClient = new AuthClient(httpClient)
      let redeemResult: Awaited<ReturnType<typeof authClient.redeemHandoffCode>>
      try {
        redeemResult = await authClient.redeemHandoffCode(handoffCode)
      } catch {
        return
      }
      const { accessToken, refreshToken, expiresAt } = redeemResult

      setToken(accessToken)
      setRefreshToken(refreshToken)
      setExpiresAt(new Date(expiresAt).getTime())
      if (mirrorLegacyAuthTokenKey) {
        localStorage.setItem(mirrorLegacyAuthTokenKey, accessToken)
      }

      // DEV-ONLY EXCEPTION (2026-08-25): skip the post-redemption verifyToken() round-trip.
      // Root cause of a cross-app SSO redirect loop hit locally: @passport/store's authStore.ts
      // owns a module-level `httpClient` singleton pointed at `VITE_API_BASE_URL ||
      // window.location.origin` — any app missing that env var, or hitting the documented
      // StrictMode double-invoke race in the login app's ReturnUrlRedirectHandler.tsx, sees this
      // call fail even though the just-redeemed token is genuinely valid (redeemHandoffCode
      // already proved that server-side — the backend just issued it). TODO: fix at the root
      // (verified per-app VITE_API_BASE_URL + the redirect race), then remove this bypass.
      let valid = true
      if (import.meta.env.DEV) {
        console.error(
          '[useSsoHandoffSession] DEV-ONLY EXCEPTION: skipping post-redemption verifyToken() — ' +
            'see comment above this line. Remove once the cross-app SSO redirect-loop bug is fixed.'
        )
      } else {
        try {
          valid = await verifyToken()
        } catch {
          return
        }
        if (!valid) {
          return
        }
      }

      httpClient.setToken(accessToken)
      try {
        const result = await new UserClient(httpClient).getProfile()
        // HttpClient.post() already unwraps the {success, data, errors} envelope, so despite
        // getProfile()'s ApiResponse<User> return type, the resolved value here is the User
        // itself, not {data: User}.
        const profile = (result as any).data ?? result
        // Real backend field names: userID, emailAddress, displayName, givenName,
        // familyName — not id/email/name/firstName/lastName.
        const newUser = {
          id: profile.userID,
          email: profile.emailAddress,
          name:
            profile.displayName ||
            (profile.givenName && profile.familyName ? `${profile.givenName} ${profile.familyName}` : profile.emailAddress),
          roles: profile.roles ?? [],
          permissions: profile.permissions ?? [],
          tenantID: profile.tenantID,
        }
        setUser(newUser)
        sessionStorage.removeItem(SSO_HANDOFF_ATTEMPT_STORAGE_KEY)
      } catch {
        // Token verified but the profile fetch failed (network blip, etc.) — leave `user`
        // unset. The route guard correctly falls back to the login redirect rather than
        // rendering a half-populated UI.
      }
    }

    reconcileSession().finally(() => {
      setIsCheckingSession(false)
    })
    // Mount-only by design: token/setToken are read once here, from whatever @passport/store
    // already rehydrated synchronously from localStorage before this component's first
    // render. Not in the dependency array on purpose — re-running this after setToken()
    // changes `token` would re-verify a second time for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isCheckingSession }
}
